// ts-rest contract of the custodian instruction of a corporate action, and of
// the Ops record of a completed corporate action (SEP-349 parts 2 and 5).
//
//   GET  /api/coac/instructions                       the state of each instruction (Ops)
//   GET  /api/coac/actions/:id/instruction            one instruction, and whether it can start
//   POST /api/coac/actions/:id/instruction/draft      generate the email from the template
//   PUT  /api/coac/actions/:id/instruction            save the edited draft
//   POST /api/coac/actions/:id/instruction/send       save, then send with the workbook
//   GET  /api/coac/actions/:id/audit-trail            each audited step of the corporate action
//
// The reply upload and the two file downloads are binary, so they sit outside
// ts-rest (`coac-instruction.controller.ts`):
//
//   POST /api/coac/actions/:id/instruction/reply              multipart `file`
//   GET  /api/coac/actions/:id/instruction/breakdown.xlsx     the client breakdown
//   GET  /api/coac/actions/:id/instruction/reply-file         the reply of the custodian
//
// Each mutation maps onto an ontology Action, and the registry audits it.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { errorBodyWithCode as errorBody } from '../schemas/common';

const c = initContract();

export const coacInstructionStatusSchema = z.enum(['draft', 'sent']);
export type CoacInstructionStatus = z.infer<typeof coacInstructionStatusSchema>;

/** What happened to the last send. `sandbox` is a scenario send: no email left the server. */
export const coacInstructionDeliverySchema = z.enum(['sent', 'sandbox', 'not_sent', 'failed']);
export type CoacInstructionDelivery = z.infer<typeof coacInstructionDeliverySchema>;

/** One option of the instruction, with its total quantity. */
export const coacInstructionElectionLineSchema = z.object({
    election: z.string(),
    quantity: z.number(),
    portfolioCount: z.number(),
});
export type CoacInstructionElectionLine = z.infer<typeof coacInstructionElectionLineSchema>;

/** The state of the instruction of one corporate action, for a list or a progress flow. */
export const coacInstructionSummarySchema = z.object({
    actionId: z.string(),
    status: coacInstructionStatusSchema,
    draftedAt: z.string(),
    sentAt: z.string().nullable(),
    sentByInitials: z.string().nullable(),
    replyAttachedAt: z.string().nullable(),
    replyReceivedAt: z.string().nullable(),
    lastDelivery: coacInstructionDeliverySchema.nullable(),
});
export type CoacInstructionSummary = z.infer<typeof coacInstructionSummarySchema>;

/**
 * The last round of the four-eye check of Ops on the instruction (SEP-360
 * section 4). `current` is false when a material response changed after the
 * submission. `canCheck` is true when the caller may approve or return the
 * round: the caller is not the preparer.
 */
export const coacInstructionCheckSchema = z.object({
    id: z.string(),
    status: z.enum(['submitted', 'approved', 'returned']),
    preparedByName: z.string().nullable(),
    submittedAt: z.string(),
    checkedByName: z.string().nullable(),
    checkedAt: z.string().nullable(),
    returnReason: z.string().nullable(),
    portfolioCount: z.number(),
    current: z.boolean(),
    canCheck: z.boolean(),
});
export type CoacInstructionCheck = z.infer<typeof coacInstructionCheckSchema>;

/** One instruction in full. */
export const coacInstructionSchema = z.object({
    id: z.string(),
    actionId: z.string(),
    status: coacInstructionStatusSchema,
    fromAddress: z.string().nullable(),
    toRecipients: z.array(z.string()),
    ccRecipients: z.array(z.string()),
    // The mailbox that the server always copies. The draft cannot remove it.
    fixedCcRecipients: z.array(z.string()),
    subject: z.string(),
    body: z.string(),
    draftedAt: z.string(),
    draftedByName: z.string().nullable(),
    editedAt: z.string().nullable(),
    lastAttemptAt: z.string().nullable(),
    lastDelivery: coacInstructionDeliverySchema.nullable(),
    lastDeliveryError: z.string().nullable(),
    sentAt: z.string().nullable(),
    sentByName: z.string().nullable(),
    sentByInitials: z.string().nullable(),
    // The options and the quantities. A draft shows the live values; a sent
    // instruction shows the values that the email stated.
    electionLines: z.array(coacInstructionElectionLineSchema),
    // The client breakdown workbook. A draft names the file that the send
    // attaches; a sent instruction names the file that the email carried.
    breakdownFileName: z.string(),
    reply: z
        .object({
            fileName: z.string(),
            receivedAt: z.string().nullable(),
            attachedAt: z.string().nullable(),
            attachedByName: z.string().nullable(),
        })
        .nullable(),
    // SEP-360 section 4. True when this deployment needs the four-eye check
    // before the send. `check` is the last round, or null.
    checkRequired: z.boolean(),
    check: coacInstructionCheckSchema.nullable(),
});
export type CoacInstruction = z.infer<typeof coacInstructionSchema>;

export const coacInstructionViewSchema = z.object({
    // True when every required response of the corporate action is in. Ops
    // generates the email only then.
    ready: z.boolean(),
    instruction: coacInstructionSchema.nullable(),
});
export type CoacInstructionView = z.infer<typeof coacInstructionViewSchema>;

export const coacInstructionEditSchema = z.object({
    toRecipients: z.array(z.string()),
    ccRecipients: z.array(z.string()),
    subject: z.string().trim().min(1),
    body: z.string().trim().min(1),
});
export type CoacInstructionEdit = z.infer<typeof coacInstructionEditSchema>;

/** One audited step of a corporate action, for the Ops record. */
export const coacAuditEventSchema = z.object({
    id: z.string(),
    occurredAt: z.string(),
    actionKey: z.string(),
    // The words of the step, for example "Instruction sent to Titan".
    label: z.string(),
    actorName: z.string().nullable(),
    actorInitials: z.string().nullable(),
});
export type CoacAuditEvent = z.infer<typeof coacAuditEventSchema>;

export const coacInstructionContract = c.router(
    {
        listInstructions: {
            method: 'GET',
            path: '/coac/instructions',
            responses: {
                200: z.object({ rows: z.array(coacInstructionSummarySchema) }),
            },
            summary: 'Ops: the state of the custodian instruction of each corporate action',
        },
        getInstruction: {
            method: 'GET',
            path: '/coac/actions/:id/instruction',
            pathParams: z.object({ id: z.string() }),
            responses: {
                200: coacInstructionViewSchema,
                404: errorBody,
            },
            summary:
                'Ops: the custodian instruction of one corporate action, and whether Ops can generate it',
        },
        draftInstruction: {
            method: 'POST',
            path: '/coac/actions/:id/instruction/draft',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}),
            responses: {
                200: coacInstructionSchema,
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary:
                'Ops: generate the custodian instruction email from the template, as a draft (gives the existing draft back)',
        },
        saveInstruction: {
            method: 'PUT',
            path: '/coac/actions/:id/instruction',
            pathParams: z.object({ id: z.string() }),
            body: coacInstructionEditSchema,
            responses: {
                200: coacInstructionSchema,
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Ops: save the edited draft of the custodian instruction',
        },
        sendInstruction: {
            method: 'POST',
            path: '/coac/actions/:id/instruction/send',
            pathParams: z.object({ id: z.string() }),
            body: coacInstructionEditSchema,
            responses: {
                200: coacInstructionSchema,
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary:
                'Ops: save the draft, then send it with the client breakdown workbook attached. The body states what happened.',
        },
        submitInstructionCheck: {
            method: 'POST',
            path: '/coac/actions/:id/instruction/check',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}),
            responses: {
                200: coacInstructionSchema,
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary:
                'Ops: submit the responses of a corporate action for the check of a second Ops user',
        },
        approveInstructionCheck: {
            method: 'POST',
            path: '/coac/actions/:id/instruction/check/approve',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}),
            responses: {
                200: coacInstructionSchema,
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Ops: a second Ops user approves the responses before the send',
        },
        returnInstructionCheck: {
            method: 'POST',
            path: '/coac/actions/:id/instruction/check/return',
            pathParams: z.object({ id: z.string() }),
            body: z.object({ reason: z.string().trim().min(1).max(2000) }),
            responses: {
                200: coacInstructionSchema,
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Ops: a second Ops user returns the responses for correction, with a reason',
        },
        auditTrail: {
            method: 'GET',
            path: '/coac/actions/:id/audit-trail',
            pathParams: z.object({ id: z.string() }),
            responses: {
                200: z.object({ events: z.array(coacAuditEventSchema) }),
                404: errorBody,
            },
            summary: 'Ops: each audited step of one corporate action, oldest first',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);

// ---------------------------------------------------------------------------
// The bold marks of the instruction body.
//
// The template of the firm puts some words in bold. The body is plain text
// that Ops edits, so the text marks bold with `**`. The server turns the
// marks into HTML for the email, and the web preview draws the same marks.
// ---------------------------------------------------------------------------

/** One run of the body text, bold or not. */
export interface BoldSegment {
    readonly text: string;
    readonly bold: boolean;
}

/**
 * Split one line of text at its `**` marks. A mark with no partner stays as
 * text, so a stray `**` never hides words.
 */
export function boldSegments(line: string): BoldSegment[] {
    const out: BoldSegment[] = [];
    let rest = line;
    while (rest.length > 0) {
        const open = rest.indexOf('**');
        const close = open >= 0 ? rest.indexOf('**', open + 2) : -1;
        if (open < 0 || close < 0) {
            out.push({ text: rest, bold: false });
            break;
        }
        if (open > 0) out.push({ text: rest.slice(0, open), bold: false });
        const inner = rest.slice(open + 2, close);
        if (inner.length > 0) out.push({ text: inner, bold: true });
        rest = rest.slice(close + 2);
    }
    return out;
}

/** The body with each `**` mark removed: the plain-text part of the email. */
export function stripBoldMarks(text: string): string {
    return text
        .split('\n')
        .map((line) =>
            boldSegments(line)
                .map((s) => s.text)
                .join(''),
        )
        .join('\n');
}
